/**
 * Passo 8 (05/10/2026): a lógica do `push` do service worker, fora dele para ter teste.
 * `silent` vem do nível do aviso ("aviso" = sem som; "som" = com o som do aparelho).
 * Onde o navegador ignora `silent`, toca o som padrão (degradação aceita na spec).
 */
export type BotaoDoPush = { action: string; title: string };

export type PayloadDoPush = {
  title?: string;
  body?: string;
  url?: string;
  icon?: string;
  badge?: string;
  tag?: string;
  silent?: boolean;
  requireInteraction?: boolean;
  notification_id?: string;
  /** Passo 18, PR 2: chamar a atenção de novo quando a tag já está na bandeja (o 2º toque depois de "Adiar"). */
  renotify?: boolean;
  /** Passo 18, PR 2: os botões do lembrete (Android e Windows mostram; o iPhone não). */
  actions?: BotaoDoPush[];
  /** Passo 18, PR 2: o lembrete e a chave de uso único do botão (vai só no push, cifrado ponta a ponta). */
  lembrete?: { id: string; token: string };
  /** Ligação do chat: o padrão de vibração do toque (o push que chama; o final, mudo, vai sem ele). */
  vibrate?: number[];
  /** Ligação do chat: o id da ligação e a chave de uso único do botão "Recusar" (só no push que chama). */
  chamada?: { id: string; token: string };
};

/** O navegador mostra no máximo 2 botões; o que não tem `action` e `title` em texto não entra. */
function botoesValidos(acoes: unknown): BotaoDoPush[] | undefined {
  if (!Array.isArray(acoes)) return undefined;
  const validos = acoes
    .filter(
      (a): a is BotaoDoPush =>
        !!a &&
        typeof (a as BotaoDoPush).action === "string" &&
        typeof (a as BotaoDoPush).title === "string"
    )
    .slice(0, 2)
    .map((a) => ({ action: a.action, title: a.title }));
  return validos.length > 0 ? validos : undefined;
}

/** Padrão de vibração válido: lista não vazia de milissegundos (números finitos, não negativos). */
function vibracaoValida(padrao: unknown): number[] | undefined {
  if (!Array.isArray(padrao) || padrao.length === 0) return undefined;
  const bom = padrao.every((ms) => typeof ms === "number" && Number.isFinite(ms) && ms >= 0);
  return bom ? (padrao as number[]) : undefined;
}

// A lib DOM do TypeScript não traz `actions`, `renotify` nem `vibrate` em NotificationOptions (o navegador os aceita).
export type OpcoesDaNotificacao = NotificationOptions & {
  actions?: BotaoDoPush[];
  renotify?: boolean;
  vibrate?: number[];
};

export function notificacaoDoPush(payload: PayloadDoPush | null): {
  title: string;
  options: OpcoesDaNotificacao;
} {
  const p = payload ?? {};
  // `silent: true` com `vibrate` lança TypeError no navegador e o push inteiro se perde: o toque só vibra se não for mudo.
  const vibrate = p.silent === true ? undefined : vibracaoValida(p.vibrate);
  return {
    title: p.title || "SE_WEB",
    options: {
      body: p.body || "",
      icon: p.icon || "/logo.png",
      badge: p.badge || "/favicon.svg?v=20260925",
      tag: p.tag,
      silent: p.silent === true,
      data: { url: p.url || "/", payload: p },
      requireInteraction: p.requireInteraction === true,
      actions: botoesValidos(p.actions),
      // O navegador lança TypeError se `renotify: true` chega sem `tag`, e o push inteiro se perde: só com tag.
      ...(p.tag && p.renotify === true ? { renotify: true } : {}),
      ...(vibrate ? { vibrate } : {}),
    },
  };
}

/** Destino do clique: a `url` do payload ou, sem ela (payload antigo/vazio), a raiz. */
export function destinoDoClique(data: { url?: string } | null | undefined): string {
  return (data && data.url) || "/";
}

export type AcaoDoClique<C> = { tipo: "focar"; cliente: C } | { tipo: "abrir"; url: string };

/**
 * O que fazer ao clicar numa notificação push (Opção B, PRD-1 §1.6.3; spec do passo 8, 5.5:
 * "mantém a regra de hoje"): focar a PRIMEIRA aba aberta cuja URL TERMINA com o destino (a URL
 * do cliente é absoluta, o destino é relativo); sem ela, abrir o destino numa janela nova.
 * Cliente sem `focus()` não conta. Pura: o service worker só executa a ação devolvida.
 */
export function acaoDoClique<C extends { url: string }>(
  destino: string,
  clientes: ReadonlyArray<C>
): AcaoDoClique<C> {
  for (const cliente of clientes) {
    if (cliente.url.endsWith(destino) && "focus" in cliente) {
      return { tipo: "focar", cliente };
    }
  }
  return { tipo: "abrir", url: destino };
}

export type AcaoDoLembrete = "concluir" | "adiar";

export interface PedidoDaAcaoDoLembrete {
  /** A rota SEM login (a chave é a credencial). */
  url: string;
  corpo: { lembrete_id: string; acao: AcaoDoLembrete; token: string };
  /** Se o pedido falhar: abrir a nota com `&acao=` — a página, logada, faz pela rota normal e diz o resultado. */
  reserva: string;
}

/**
 * O botão do lembrete no push (passo 18, PR 2): o POST sem login e a reserva. `null` = não é botão de lembrete (clique
 * no corpo, `event.action` vazio, push sem `lembrete`): vale a regra de clique de hoje. `baseDaApi` é o `VITE_API_URL`
 * do build (relativo na produção, absoluto em dev; vazio vira `/api/v1`), resolvido contra a origem do service worker.
 */
export function pedidoDaAcaoDoLembrete(
  data: unknown,
  acao: string,
  baseDaApi: string,
  origem: string
): PedidoDaAcaoDoLembrete | null {
  if (acao !== "concluir" && acao !== "adiar") return null;
  const dados = (data ?? {}) as { url?: string; payload?: PayloadDoPush };
  const lembrete = dados.payload?.lembrete;
  if (!lembrete || typeof lembrete.id !== "string" || typeof lembrete.token !== "string")
    return null;
  if (!lembrete.id || !lembrete.token) return null;
  const base = (baseDaApi || "/api/v1").replace(/\/+$/, "");
  const destino = destinoDoClique(dados);
  return {
    url: new URL(`${base}/public/lembretes/acao`, origem).toString(),
    corpo: { lembrete_id: lembrete.id, acao, token: lembrete.token },
    reserva: `${destino}${destino.includes("?") ? "&" : "?"}acao=${acao}&lembrete=${encodeURIComponent(lembrete.id)}`,
  };
}

/**
 * A ligação do chat que chega com a página aberta e à vista: candidata a tocar pela página (toque, aviso e janelinha),
 * e então o celular não precisa tocar também. `true` só quando o push é de ligação (tem `chamada` ou a `tag` começa com
 * `chamada-`: o push final, mudo, só tem a tag) E alguma janela está `visible`. Push comum dá sempre `false`: o
 * aviso de hoje não muda.
 *
 * `true` NÃO é "a página toca": uma janela à vista pode ser a tela de login, ou uma aba com o WebSocket do sino
 * caído, e nenhuma das duas toca. Quem decide é a página, por pergunta e resposta (`perguntarSeATocar`). E `true`
 * tampouco é "não mostre nada": o push SEMPRE mostra uma notificação (ver `mostrarOPush`), só que discreta.
 */
export function pushDaChamadaComJanelaAVista(
  payload: PayloadDoPush | null,
  clientes: ReadonlyArray<{ visibilityState: string }>
): boolean {
  return (
    ehPayloadDeChamada(payload) && clientes.some((cliente) => cliente.visibilityState === "visible")
  );
}

/** O push é de ligação do chat quando tem `chamada` (o que toca) ou a `tag` começa com `chamada-` (o final, mudo, só tem a tag). */
function ehPayloadDeChamada(payload: PayloadDoPush | null | undefined): boolean {
  const tag = payload?.tag;
  return !!payload?.chamada || (typeof tag === "string" && tag.startsWith("chamada-"));
}

/** `ehPayloadDeChamada` sobre o `data` da notificação (`{ url, payload }`): é com ele que o clique decide. */
export function ehPushDeChamada(data: unknown): boolean {
  return ehPayloadDeChamada((data as { payload?: PayloadDoPush } | null | undefined)?.payload);
}

/**
 * As opções da notificação que só existe para cumprir a regra de "todo push mostra uma": muda, sem vibrar e sem
 * ficar fixa na tela. Pura (não muda a entrada): o service worker ainda precisa das opções inteiras para o caso
 * em que nenhuma janela confirma que vai tocar.
 */
export function opcoesDiscretas(o: OpcoesDaNotificacao): OpcoesDaNotificacao {
  const discretas: OpcoesDaNotificacao = { ...o, silent: true };
  delete discretas.vibrate;
  delete discretas.requireInteraction;
  return discretas;
}

/** O `type` da pergunta que o service worker faz à página aberta: "você vai tocar esta ligação?". */
export const TIPO_DA_PERGUNTA_DA_CHAMADA = "chamada:vai-tocar";

/** Quanto o service worker espera a resposta da página antes de contar como "não". Curto: o aparelho não pode demorar a tocar. */
export const PRAZO_DA_CONFIRMACAO_MS = 800;

/**
 * O que a página responde, pela porta que veio com a pergunta (`evento.ports[0]`). O contrato mora aqui, nos dois lados.
 * `tocar`: o sino está de pé (WebSocket ou SSE), então esta página vai tocar a ligação: o push usa esta. `aberta`: a
 * página existe e tem o `CallProvider` montado (é o que o clique no aviso usa). Quem não tem `CallProvider` (login,
 * portal, manutenção) não responde, e o silêncio vale como "não".
 */
export interface RespostaDaPerguntaDaChamada {
  tocar: boolean;
  aberta: true;
}

/** A página confere o que chega pelo `navigator.serviceWorker`: só a pergunta do service worker é respondida. */
export function ehPerguntaDaChamada(dado: unknown): boolean {
  return (dado as { tipo?: unknown } | null | undefined)?.tipo === TIPO_DA_PERGUNTA_DA_CHAMADA;
}

/** O que `perguntarSeATocar` pede de uma janela do app (`WindowClient`). */
export interface JanelaQueResponde {
  visibilityState: string;
  postMessage(mensagem: unknown, transferir?: Transferable[]): void;
}

/**
 * O miolo da pergunta à página, por um `MessageChannel` (a resposta volta pela porta que vai junto): a mesma pergunta
 * serve ao push (`perguntarSeATocar`, que lê `tocar`) e ao clique (`perguntarSeEstaAberta`, que lê `aberta`), e só o
 * campo da resposta que vale muda. `true` só se ela responde `{ [campo]: true }` antes do prazo. O prazo vencido,
 * `{ [campo]: false }`, qualquer outra resposta, um `postMessage` que lança e um canal que não se cria dão `false`;
 * nunca rejeita. Resposta que chega depois do prazo é ignorada (a porta já foi fechada).
 */
function perguntarAPagina(
  janela: JanelaQueResponde,
  campo: keyof RespostaDaPerguntaDaChamada,
  prazoMs: number,
  criarCanal: () => MessageChannel
): Promise<boolean> {
  return new Promise<boolean>((resolve) => {
    let canal: MessageChannel | undefined;
    let prazo: ReturnType<typeof setTimeout> | undefined;
    const responder = (sim: boolean) => {
      clearTimeout(prazo);
      if (canal) {
        canal.port1.onmessage = null;
        try {
          canal.port1.close();
        } catch {
          // porta já fechada: nada a fazer
        }
      }
      resolve(sim); // a 2ª chamada (resposta tardia) não muda a promessa que já se resolveu
    };
    try {
      canal = criarCanal();
      canal.port1.onmessage = (evento) =>
        responder((evento.data as Partial<RespostaDaPerguntaDaChamada> | null)?.[campo] === true);
      prazo = setTimeout(() => responder(false), prazoMs);
      janela.postMessage({ tipo: TIPO_DA_PERGUNTA_DA_CHAMADA }, [canal.port2]);
    } catch {
      responder(false);
    }
  });
}

/**
 * Pergunta à página se ela vai tocar a ligação (o push usa esta). `true` só se ela responde `{ tocar: true }` antes do
 * prazo; o resto é de `perguntarAPagina`. `criarCanal` existe para o teste.
 */
export function perguntarSeATocar(
  janela: JanelaQueResponde,
  prazoMs: number = PRAZO_DA_CONFIRMACAO_MS,
  criarCanal: () => MessageChannel = () => new MessageChannel()
): Promise<boolean> {
  return perguntarAPagina(janela, "tocar", prazoMs, criarCanal);
}

/**
 * Pergunta à página se ela está aberta, com o `CallProvider` montado (o clique no aviso usa esta). `true` só se ela
 * responde `{ aberta: true }` antes do prazo. Não é `perguntarSeATocar`: uma aba EM LIGAÇÃO com o sino caído responde
 * `{ tocar: false, aberta: true }`, e é justamente ela que o clique não pode recarregar (`WindowClient.navigate` derrubaria
 * a ligação). Quem não tem `CallProvider` (login, portal, manutenção) não responde, e o silêncio vale `false`.
 */
export function perguntarSeEstaAberta(
  janela: JanelaQueResponde,
  prazoMs: number = PRAZO_DA_CONFIRMACAO_MS,
  criarCanal: () => MessageChannel = () => new MessageChannel()
): Promise<boolean> {
  return perguntarAPagina(janela, "aberta", prazoMs, criarCanal);
}

/**
 * Pergunta só às janelas `visible` (as escondidas não tocam), todas ao mesmo tempo, e devolve `true` assim que uma
 * confirma: a aba de login ao lado, que nunca responde, não atrasa quem já disse que vai tocar. `false` quando nenhuma
 * confirma (ou não há janela à vista). `confirmar` que rejeita ou lança conta como "não".
 */
export function algumaJanelaConfirma(
  janelas: ReadonlyArray<JanelaQueResponde>,
  confirmar: (janela: JanelaQueResponde) => Promise<boolean>
): Promise<boolean> {
  const visiveis = janelas.filter((janela) => janela.visibilityState === "visible");
  if (visiveis.length === 0) return Promise.resolve(false);
  return new Promise<boolean>((resolve) => {
    let faltam = visiveis.length;
    for (const janela of visiveis) {
      void (async () => {
        try {
          return await confirmar(janela);
        } catch {
          return false;
        }
      })().then((confirmou) => {
        if (confirmou) resolve(true);
        else if (--faltam === 0) resolve(false);
      });
    }
  });
}

/** O que `mostrarOPush` pede do registro do service worker (`self.registration`). */
export interface RegistroParaMostrar {
  showNotification(title: string, options?: OpcoesDaNotificacao): Promise<void>;
  getNotifications(filter?: { tag?: string }): Promise<ReadonlyArray<{ close(): void }>>;
}

/**
 * Mostra o push. SEMPRE chama `showNotification` (uma vez): o Safari/iOS revoga a inscrição de quem recebe push e
 * não mostra nada, e o Chrome mostra a dele ("este site foi atualizado em segundo plano"). Por isso a página nunca
 * "abafa" um push: no máximo ele sai discreto.
 *
 * Ligação do chat (`pushDaChamadaComJanelaAVista`) com uma janela à vista que CONFIRMA que vai tocar (`confirmar`, que
 * no service worker é `perguntarSeATocar`): sai com as opções discretas (mudas, sem vibrar nem fixar).
 *  - o push que toca (`chamada`): mesma `tag` e `renotify: false`, e NÃO fecha. As rodadas seguintes (a cada 5 s)
 *    substituem a notificação da mesma tag sem chamar a atenção; mostrar e fechar a cada rodada fazia a bandeja piscar.
 *  - o push final (sem `chamada`): mostra o discreto e FECHA pela `tag`. É ele quem tira a discreta da bandeja (todo
 *    tocado recebe o push final). Sem `tag` não fecha nada (`getNotifications` sem filtro devolveria a bandeja inteira);
 *    o servidor sempre manda `chamada-<id>`.
 *
 * Sem janela à vista, ou sem nenhuma confirmando (a tela de login, uma aba com o sino caído, a página que não
 * respondeu no prazo), e para todo push que não é de ligação, sai a notificação inteira, como sempre saiu. O padrão de
 * `confirmar` é "ninguém confirma". Se a lista de janelas falhar, vale "nenhuma à vista".
 */
export async function mostrarOPush(
  registro: RegistroParaMostrar,
  listarJanelas: () => Promise<ReadonlyArray<JanelaQueResponde>>,
  payload: PayloadDoPush | null,
  confirmar: (janela: JanelaQueResponde) => Promise<boolean> = () => Promise.resolve(false)
): Promise<void> {
  const { title, options } = notificacaoDoPush(payload);
  let janelas: ReadonlyArray<JanelaQueResponde> = [];
  try {
    janelas = await listarJanelas();
  } catch {
    // sem a lista, a notificação inteira
  }
  if (
    !pushDaChamadaComJanelaAVista(payload, janelas) ||
    !(await algumaJanelaConfirma(janelas, confirmar))
  ) {
    await registro.showNotification(title, options);
    return;
  }
  if (payload?.chamada) {
    // `renotify: true` do toque chamaria a atenção a cada rodada; a página já está tocando.
    await registro.showNotification(title, { ...opcoesDiscretas(options), renotify: false });
    return;
  }
  await registro.showNotification(title, opcoesDiscretas(options));
  if (!options.tag) return;
  for (const aberta of await registro.getNotifications({ tag: options.tag })) aberta.close();
}

/** O `type` da mensagem que o service worker manda à página aberta quando a pessoa toca no aviso da ligação. */
export const TIPO_DA_MENSAGEM_DA_CHAMADA = "chamada:abrir";

/**
 * O que o service worker diz à página aberta (`client.postMessage`) quando a pessoa toca no corpo do aviso da ligação
 * ou em "Atender": abrir o aviso daquela ligação e, com `atender`, já atendê-la. O contrato mora aqui, nos dois lados.
 */
export interface MensagemDaChamada {
  tipo: typeof TIPO_DA_MENSAGEM_DA_CHAMADA;
  call_session_id: string;
  /** `true` para o botão "Atender"; `false` para o clique no corpo (a pessoa só quer ver a ligação). */
  atender: boolean;
}

/** A página confere o que chega pelo `navigator.serviceWorker`: qualquer outra mensagem é ignorada. */
export function ehMensagemDaChamada(dado: unknown): dado is MensagemDaChamada {
  const m = dado as Partial<MensagemDaChamada> | null | undefined;
  return (
    !!m &&
    m.tipo === TIPO_DA_MENSAGEM_DA_CHAMADA &&
    typeof m.call_session_id === "string" &&
    m.call_session_id !== "" &&
    typeof m.atender === "boolean"
  );
}

/** O `type` da mensagem que o service worker manda à página aberta quando a pessoa toca no push FINAL da ligação. */
export const TIPO_DA_MENSAGEM_DE_IR = "chamada:ir";

/**
 * O que o service worker diz à página aberta no clique do push final ("Chamada perdida", "Ligação atendida"): ir até a
 * `url` pelo router da própria página. O service worker não navega essa janela: `WindowClient.navigate` RECARREGA a
 * página e derrubaria uma ligação em curso. O contrato mora aqui, nos dois lados.
 */
export interface MensagemDeIr {
  tipo: typeof TIPO_DA_MENSAGEM_DE_IR;
  url: string;
}

/**
 * Caminho do app, e só ele: começa com `/` e não com `//`. O navegador lê `\` como `/` e descarta tabulação e quebra de
 * linha ao resolver uma URL, então `/\evil.com` e `/<tab>/evil.com` também viram `//evil.com` (outra origem); por isso
 * barra invertida, espaço e caractere de controle não passam.
 */
function ehCaminhoDoApp(url: unknown): url is string {
  if (typeof url !== "string" || !url.startsWith("/") || url.startsWith("//")) return false;
  return ![...url].some((c) => {
    const codigo = c.charCodeAt(0);
    return c === "\\" || codigo <= 32 || codigo === 127;
  });
}

/** A página confere o que chega pelo `navigator.serviceWorker`: só o `chamada:ir` com um caminho do app navega. */
export function ehMensagemDeIr(dado: unknown): dado is MensagemDeIr {
  const m = dado as Partial<MensagemDeIr> | null | undefined;
  return !!m && m.tipo === TIPO_DA_MENSAGEM_DE_IR && ehCaminhoDoApp(m.url);
}

export type AcaoDoCliqueNaChamada<J> =
  | { tipo: "focar"; janela: J; mensagem: MensagemDaChamada | MensagemDeIr }
  | { tipo: "abrir"; url: string };

/** A janela do app que o clique foca: a que tem `focus()` e, de preferência, está visível; senão a primeira (a mais recente). */
function melhorJanela<J extends { visibilityState: string }>(
  janelas: ReadonlyArray<J>
): J | undefined {
  const aptas = janelas.filter((j) => typeof (j as { focus?: unknown }).focus === "function");
  return aptas.find((j) => j.visibilityState === "visible") ?? aptas[0];
}

/**
 * O que fazer ao tocar no "Atender" ou no corpo do aviso de uma LIGAÇÃO (o push que toca, com `chamada`). `null` = não é
 * isso (o "Recusar" tem o `pedidoDaAcaoDaChamada`; o push final tem o `destinoDoCliqueNoPushFinal`; o lembrete e os
 * avisos comuns seguem a regra de sempre, `acaoDoClique`, que não muda).
 *
 * Por que não vale o `acaoDoClique`: ele só foca a aba cuja URL TERMINA com o destino, e a página tira o `?ligacao=` da
 * URL assim que o lê. Com o SE_WEB numa aba de fundo (o caso comum) nenhuma casa, e o clique abria uma SEGUNDA
 * instância do app: duas tocando, dois WebSockets. Aqui, havendo qualquer janela do app (o `matchAll` só devolve as da
 * mesma origem, a mais recentemente focada primeiro), foca-se uma — de preferência uma visível — e a mensagem leva a
 * ligação até a página, que já está aberta. Sem janela, abre-se a `url` do payload (que leva `?ligacao=`). Janela sem
 * `focus()` não conta. Pura: o service worker só executa.
 */
export function destinoDoCliqueNaChamada<J extends { visibilityState: string }>(
  data: unknown,
  acao: string,
  janelas: ReadonlyArray<J>
): AcaoDoCliqueNaChamada<J> | null {
  if (acao !== "" && acao !== "atender") return null;
  const dados = (data ?? {}) as { url?: string; payload?: PayloadDoPush };
  const chamada = dados.payload?.chamada;
  if (!chamada || typeof chamada.id !== "string" || !chamada.id) return null;
  const janela = melhorJanela(janelas);
  if (!janela) return { tipo: "abrir", url: destinoDoClique(dados) };
  return {
    tipo: "focar",
    janela,
    mensagem: {
      tipo: TIPO_DA_MENSAGEM_DA_CHAMADA,
      call_session_id: chamada.id,
      atender: acao === "atender",
    },
  };
}

/**
 * O que fazer ao tocar no corpo do push FINAL de uma ligação ("Chamada perdida", "Ligação atendida": `ehPushDeChamada`
 * sem `chamada`, sem botões, `acao === ""`). `null` = não é isso (o push que toca é do `destinoDoCliqueNaChamada`; o
 * lembrete e os avisos comuns seguem a regra de sempre, `acaoDoClique`).
 *
 * Mesma escolha da janela do `destinoDoCliqueNaChamada` (qualquer janela do app, de preferência a visível), mas a
 * mensagem é `chamada:ir`: a página anda até a `url` pelo router. Sem janela, abre-se a `url`. Pura: o service worker
 * só executa, e decide `navigate` ou não conforme a janela confirme.
 */
export function destinoDoCliqueNoPushFinal<J extends { visibilityState: string }>(
  data: unknown,
  acao: string,
  janelas: ReadonlyArray<J>
): AcaoDoCliqueNaChamada<J> | null {
  if (acao !== "") return null;
  const dados = (data ?? {}) as { url?: string; payload?: PayloadDoPush };
  if (!ehPushDeChamada(dados) || dados.payload?.chamada) return null;
  const url = destinoDoClique(dados);
  const janela = melhorJanela(janelas);
  if (!janela) return { tipo: "abrir", url };
  return { tipo: "focar", janela, mensagem: { tipo: TIPO_DA_MENSAGEM_DE_IR, url } };
}

/** O que `tratarCliqueNaChamada` pede de uma janela do app (`WindowClient`). */
export interface JanelaDaChamada extends JanelaQueResponde {
  focus(): Promise<unknown>;
  /** `WindowClient.navigate`: RECARREGA a página (e rejeita numa janela que o service worker não controla). */
  navigate?(url: string): Promise<unknown>;
}

/**
 * Executa o `destinoDoCliqueNaChamada` (o toque: corpo e "Atender") ou o `destinoDoCliqueNoPushFinal` (o push final)
 * com as dependências do service worker injetadas. Devolve `false` quando o clique NÃO é de uma ligação (nada foi
 * feito, nem a lista de janelas foi pedida: o chamador segue a regra de sempre) e `true` quando tratou.
 *
 * Havendo janela do app, foca-se a melhor e `confirmar` diz se ELA tem a página de ligação (no service worker é
 * `perguntarSeEstaAberta`, o `CallProvider` montado; o padrão é "sim"):
 *  - confirmou: `postMessage` leva a ligação (`chamada:abrir`) ou a `url` (`chamada:ir`) até a página, que já está lá. A
 *    janela confirmada NUNCA é navegada: `navigate` recarregaria a página e derrubaria uma ligação em curso.
 *  - não confirmou (login, portal, manutenção: sem `CallProvider`, então não há ligação a derrubar): `navigate(url)`, e
 *    a `url` leva `?ligacao=`. Sem `navigate`, ou se ele lançar, abre-se a `url` numa janela nova.
 * Se o `focus` ou o `postMessage` lançar, abre a `url` como se não houvesse janela; `confirmar` que lança conta como
 * "não confirmou"; se a lista de janelas falhar, vale "nenhuma janela". Se a reserva (`abrirJanela`) for recusada
 * DEPOIS de um `focus` que passou, a rejeição é engolida: a janela já foi focada e o clique não pode rejeitar.
 */
export async function tratarCliqueNaChamada(
  data: unknown,
  acao: string,
  listarJanelas: () => Promise<ReadonlyArray<JanelaDaChamada>>,
  abrirJanela: (url: string) => Promise<unknown>,
  confirmar: (janela: JanelaDaChamada) => Promise<boolean> = async () => true
): Promise<boolean> {
  const decidir = (janelas: ReadonlyArray<JanelaDaChamada>) =>
    destinoDoCliqueNaChamada(data, acao, janelas) ??
    destinoDoCliqueNoPushFinal(data, acao, janelas);
  if (!decidir([])) return false;
  let janelas: ReadonlyArray<JanelaDaChamada> = [];
  try {
    janelas = await listarJanelas();
  } catch {
    // sem a lista, abre a url
  }
  const decisao = decidir(janelas);
  const url = destinoDoClique(data as { url?: string });
  /** A janela já foi focada: o `openWindow` de reserva que vem depois pode ser recusado, e isso não é falha. */
  let focou = false;
  if (decisao?.tipo === "focar") {
    const { janela } = decisao;
    try {
      // Foca primeiro: o foco só vale dentro do clique, e a aba em segundo plano acorda para responder à pergunta.
      await janela.focus();
      focou = true;
      let temAPagina = false;
      try {
        temAPagina = await confirmar(janela);
      } catch {
        // a pergunta falhou: sem resposta, vale "não"
      }
      if (temAPagina) {
        janela.postMessage(decisao.mensagem);
        return true;
      }
      if (typeof janela.navigate === "function") {
        await janela.navigate(url);
        return true;
      }
    } catch {
      // a janela não respondeu ou não deixou navegar: abre a url, que leva ?ligacao=
    }
  }
  try {
    await abrirJanela(url);
  } catch (erro) {
    // Depois de um `focus()` que passou, o Chromium consome a interação de janela e recusa o `openWindow` seguinte
    // ("Not allowed to open a window"). A janela já está focada onde estava, e a rejeição derrubaria o `waitUntil`
    // do clique: aqui ela se engole. Sem foco (nenhuma janela, ou o `focus` falhou) o `openWindow` é o único
    // caminho, e a falha segue para quem chamou.
    if (!focou) throw erro;
  }
  return true;
}

export interface PedidoDaAcaoDaChamada {
  /** A rota SEM login (a chave é a credencial). */
  url: string;
  /** O corpo de `RecusaDoPush` no servidor (`extra="forbid"`): só estes dois campos. */
  corpo: { call_session_id: string; token: string };
  /** Se o pedido falhar: a conversa com `?ligacao=`, e a página, logada, mostra o aviso (ou diz que a ligação acabou). */
  reserva: string;
}

/**
 * O botão "Recusar" da ligação no push: o POST sem login e a reserva. `null` = não é o "Recusar" de uma ligação
 * (o "Atender", o clique no corpo, `event.action` vazio, push sem `chamada`): vale a regra de clique de hoje, que
 * abre ou foca a `url` (com `?ligacao=`). Só o "Recusar" age sem abrir a página: atender exige a pessoa na página
 * (o microfone e a câmera pedem permissão ali). `baseDaApi` é o `VITE_API_URL` do build, que JÁ traz `/api/v1`
 * (relativo na produção, absoluto em dev; vazio vira `/api/v1`), resolvido contra a origem do service worker.
 */
export function pedidoDaAcaoDaChamada(
  data: unknown,
  acao: string,
  baseDaApi: string,
  origem: string
): PedidoDaAcaoDaChamada | null {
  if (acao !== "recusar") return null;
  const dados = (data ?? {}) as { url?: string; payload?: PayloadDoPush };
  const chamada = dados.payload?.chamada;
  if (!chamada || typeof chamada.id !== "string" || typeof chamada.token !== "string") return null;
  if (!chamada.id || !chamada.token) return null;
  const base = (baseDaApi || "/api/v1").replace(/\/+$/, "");
  return {
    url: new URL(`${base}/public/chamadas/recusar`, origem).toString(),
    corpo: { call_session_id: chamada.id, token: chamada.token },
    reserva: destinoDoClique(dados),
  };
}

/** O `type` da mensagem que o service worker manda a TODAS as janelas quando a inscrição de push do aparelho girou. */
export const TIPO_DA_MENSAGEM_DE_INSCRICAO_MUDOU = "push:inscricao-mudou";

/**
 * O que o `pushsubscriptionchange` traz, só no que importa aqui (o `PushSubscriptionChangeEvent` do navegador cabe
 * nesta forma). `newSubscription` vem preenchida quando o próprio navegador já reinscreveu; quando não, vem vazia, e a
 * chave do servidor (VAPID) só se recupera da inscrição antiga.
 */
export interface EventoDeMudancaDaInscricao {
  oldSubscription?: { options?: { applicationServerKey?: ArrayBuffer | null } } | null;
  newSubscription?: unknown | null;
}

/**
 * O que o service worker conseguiu fazer com a inscrição que girou: `o-navegador-reinscreveu` (veio `newSubscription`,
 * nada a fazer), `reinscrito-aqui` (refeita com a chave da antiga) ou `sem-como-reinscrever` (sem chave, ou o
 * `subscribe` recusou). Nos três a página é avisada: é ela, com a sessão, que sincroniza com o servidor.
 */
export type ResultadoDaMudanca =
  | "o-navegador-reinscreveu"
  | "reinscrito-aqui"
  | "sem-como-reinscrever";

/**
 * `pushsubscriptionchange`: reinscreve no navegador e acorda as janelas abertas com
 * `postMessage({ tipo: TIPO_DA_MENSAGEM_DE_INSCRICAO_MUDOU })`. O service worker NÃO manda a inscrição nova ao
 * servidor: ele não tem sessão (o token mora no `localStorage` da página), e um endpoint público autorizado pela posse
 * do endpoint antigo abriria uma escrita sem login. Com o app fechado, o servidor aprende a inscrição nova na próxima
 * abertura. A mensagem leva só o tipo: endpoint e chaves não saem daqui. Reinscreve ANTES de avisar, para a página que
 * acorda já encontrar a inscrição nova. Nunca lança: o evento é do navegador, e um erro aqui só derrubaria o
 * `waitUntil`; um `postMessage` que falha não impede o aviso às outras janelas.
 */
export async function tratarMudancaDaInscricao(
  evento: EventoDeMudancaDaInscricao,
  registro: {
    pushManager: {
      subscribe(opcoes: {
        userVisibleOnly: boolean;
        applicationServerKey: ArrayBuffer;
      }): Promise<unknown>;
    };
  },
  listarJanelas: () => Promise<ReadonlyArray<{ postMessage(mensagem: unknown): void }>>
): Promise<ResultadoDaMudanca> {
  let resultado: ResultadoDaMudanca = "sem-como-reinscrever";
  if (evento.newSubscription) {
    resultado = "o-navegador-reinscreveu";
  } else {
    const chave = evento.oldSubscription?.options?.applicationServerKey;
    if (chave) {
      try {
        await registro.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: chave,
        });
        resultado = "reinscrito-aqui";
      } catch {
        // o navegador recusou (permissão tirada, sem rede): fica "sem-como-reinscrever" e a página decide
      }
    }
  }
  let janelas: ReadonlyArray<{ postMessage(mensagem: unknown): void }> = [];
  try {
    janelas = await listarJanelas();
  } catch {
    // sem a lista, ninguém para avisar: a página sincroniza na próxima abertura
  }
  for (const janela of janelas) {
    try {
      janela.postMessage({ tipo: TIPO_DA_MENSAGEM_DE_INSCRICAO_MUDOU });
    } catch {
      // uma janela que não recebe não impede as outras
    }
  }
  return resultado;
}
